// Inline consent + review for recording a device's web UI.
//
// Inline rather than a modal on purpose. This sits inside the Web UI tab,
// next to the thing it describes, and the user can ignore it and carry on
// browsing — consent to help us is not a toll on reaching your own switch.
//
// The disclosure text is a module constant and is POSTed verbatim with the
// consent, so the stored record reflects what was actually on screen
// rather than what a later version of this file happens to say.

import { useState } from 'react';
import { AlertCircle, CheckCircle2, Circle, Loader2, ShieldCheck, Trash2 } from 'lucide-react';
import type { CaptureStage, CaptureSummary } from '../hooks/useWebCapture';
import { type CaptureReason, disclosureFor } from '@/lib/webCaptureDisclosure';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

interface Props {
  stage: CaptureStage;
  /**
   * Why we are asking. Decides the heading, the disclosure wording, and
   * whether we ask what the customer was trying to do — a recording someone
   * started themselves is the only one where we do not already know.
   */
  reason: CaptureReason;
  /** The specific thing we asked to be shown, when `reason` is `requested`. */
  ask: string | null;
  summary: CaptureSummary | null;
  error: string | null;
  busy: boolean;
  note: string;
  onNoteChange: (note: string) => void;
  onStart: () => void;
  onStop: () => void;
  onApprove: () => void;
  onDiscard: () => void;
  onDismiss: () => void;
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="flex flex-col">
      <span className="text-lg font-semibold tabular-nums">{value}</span>
      <span className="text-xs text-muted-foreground">{label}</span>
    </div>
  );
}

export default function WebCaptureConsent({
  stage,
  reason,
  ask,
  summary,
  error,
  busy,
  note,
  onNoteChange,
  onStart,
  onStop,
  onApprove,
  onDiscard,
  onDismiss,
}: Props) {
  const [showPages, setShowPages] = useState(false);

  if (stage === 'done') {
    return (
      <Alert className="mb-3">
        <CheckCircle2 className="h-4 w-4" />
        <AlertDescription className="flex items-center justify-between gap-3">
          <span>Thanks — that is all we need. You can keep using the web UI as normal.</span>
          <Button variant="ghost" size="sm" onClick={onDismiss}>
            Dismiss
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="mb-3 rounded-md border border-border bg-muted/30 p-3">
      {error ? (
        <Alert variant="destructive" className="mb-3">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      {/* --- Offer --- */}
      {stage === 'off' || stage === 'starting' ? (
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-brand" />
            <span className="text-sm font-medium">
              {reason === 'unidentified'
                ? 'Help us support this device'
                : reason === 'requested'
                  ? 'Help us improve this device'
                  : 'Record this session'}
            </span>
          </div>
          <p className="text-sm text-muted-foreground whitespace-pre-line">
            {disclosureFor(reason, ask ?? undefined)}
          </p>

          {/*
            Only asked when the customer started this themselves. A prompted
            recording answers a question we posed, so we already know what it
            is for; a volunteered one is otherwise a few dozen HTTP exchanges
            with no question attached, and nobody downstream can act on that.
            Optional, and deliberately not enforced — a recording must never be
            blocked on writing prose.
          */}
          {reason === 'userInitiated' ? (
            <div className="flex flex-col gap-1.5 pt-1">
              <Label htmlFor="capture-note" className="text-xs font-normal text-muted-foreground">
                What are you trying to do? (optional, but it helps a lot)
              </Label>
              <Textarea
                id="capture-note"
                value={note}
                onChange={(e) => { onNoteChange(e.target.value); }}
                placeholder="e.g. Setting a port to a VLAN, but the change never sticks"
                rows={2}
                disabled={busy || stage === 'starting'}
              />
            </div>
          ) : null}

          <div className="flex items-center gap-2 pt-1">
            <Button size="sm" disabled={busy || stage === 'starting'} onClick={onStart}>
              {stage === 'starting' ? (
                <>
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  Reconnecting…
                </>
              ) : (
                reason === 'requested' ? 'Record and show us' : 'Start recording'
              )}
            </Button>
            <Button variant="ghost" size="sm" onClick={onDismiss} disabled={busy}>
              {reason === 'unidentified' ? 'Not now' : 'Cancel'}
            </Button>
          </div>
        </div>
      ) : null}

      {/* --- Recording --- */}
      {stage === 'recording' ? (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Circle className="h-3 w-3 animate-pulse fill-sev-1 text-sev-1" />
            <span className="text-sm font-medium">Recording this session</span>
            <span className="text-sm text-muted-foreground">
              Log in and visit the pages you would normally use. Stop whenever you like.
            </span>
          </div>
          <Button size="sm" variant="outline" disabled={busy} onClick={onStop}>
            Stop and review
          </Button>
        </div>
      ) : null}

      {/* --- Review --- */}
      {stage === 'review' && summary ? (
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-brand" />
            <span className="text-sm font-medium">Review before sharing</span>
            <Badge className="border-0 bg-muted font-normal text-muted-foreground">
              nothing sent yet
            </Badge>
          </div>

          <div className="flex flex-wrap gap-6">
            <Stat label="pages visited" value={summary.pageUrls.length} />
            <Stat label="requests recorded" value={summary.exchangeCount} />
            <Stat label="secrets removed" value={summary.redaction.total} />
          </div>

          {summary.redaction.total > 0 ? (
            <p className="text-xs text-muted-foreground">
              Removed on your gateway: {summary.redaction.headers} header
              {summary.redaction.headers === 1 ? '' : 's'}, {summary.redaction.cookies} cookie
              {summary.redaction.cookies === 1 ? '' : 's'}, {summary.redaction.bodyFields} form
              field{summary.redaction.bodyFields === 1 ? '' : 's'},{' '}
              {summary.redaction.queryParams} query parameter
              {summary.redaction.queryParams === 1 ? '' : 's'}
              {summary.redaction.literalMatches > 0
                ? `, and ${String(summary.redaction.literalMatches)} occurrence${
                    summary.redaction.literalMatches === 1 ? '' : 's'
                  } of a stored credential`
                : ''}
              .
            </p>
          ) : (
            // Zero is worth calling out rather than leaving blank — it
            // means the pages held nothing secret, not that scrubbing
            // failed to run.
            <p className="text-xs text-muted-foreground">
              No credentials were found in these pages, so there was nothing to remove.
            </p>
          )}

          <div>
            <button
              type="button"
              className="text-xs text-brand underline underline-offset-2"
              onClick={() => { setShowPages(!showPages); }}
            >
              {showPages ? 'Hide' : 'Show'} the {summary.pageUrls.length} page
              {summary.pageUrls.length === 1 ? '' : 's'} this would share
            </button>
            {showPages ? (
              <ul className="mt-2 max-h-40 overflow-y-auto rounded border border-border-subtle bg-background p-2">
                {summary.pageUrls.map((u) => (
                  <li key={u} className="truncate font-mono text-xs text-muted-foreground">
                    {u}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>

          <div className="flex items-center gap-2">
            <Button size="sm" disabled={busy} onClick={onApprove}>
              Share with support
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5 text-sev-1 hover:text-sev-1"
              disabled={busy}
              onClick={onDiscard}
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete it
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            This recording is on your gateway and has not left your network. If you do
            nothing it is deleted automatically.
          </p>
        </div>
      ) : null}
    </div>
  );
}
